import React, { useMemo, useState } from 'react';
import { theme } from '../../styles/theme';
import { CARD_TEMPLATES } from '../../data/cards';
import { Card, CardCategory } from '../../types/game';
import { CardCategoryIcon } from '../ui/CardCategoryIcon';

interface CardsReferenceScreenProps {
  onBack: () => void;
}

// NOTE: 'junk' is intentionally omitted from CATEGORY_ORDER so junk cards
// (e.g. Corrupted Intel) never appear in the reference screen. The records
// below still include a junk entry to satisfy the Record<CardCategory, ...>
// completeness check.
const CATEGORY_ORDER: CardCategory[] = ['movement', 'intel', 'tactics', 'combat', 'fieldcraft'];

const CATEGORY_LABELS: Record<CardCategory, string> = {
  movement: 'Movement',
  intel: 'Intel',
  tactics: 'Tactics',
  combat: 'Combat',
  fieldcraft: 'Fieldcraft',
  junk: 'Junk',
};

const CATEGORY_COLORS: Record<CardCategory, string> = {
  movement: '#66ccff',
  intel: '#aa88ff',
  tactics: '#ffaa44',
  combat: '#ff5555',
  fieldcraft: '#44cc88',
  junk: '#777777',
};

export function CardsReferenceScreen({ onBack }: CardsReferenceScreenProps) {
  const [filter, setFilter] = useState<CardCategory | 'all'>('all');

  const grouped = useMemo(() => {
    const byCategory: Record<CardCategory, Array<Omit<Card, 'id'>>> = {
      movement: [], intel: [], tactics: [], combat: [], fieldcraft: [], junk: [],
    };
    for (const template of Object.values(CARD_TEMPLATES)) {
      // Skip junk entirely — not player-facing
      if (template.category === 'junk') continue;
      byCategory[template.category].push(template);
    }
    for (const cat of CATEGORY_ORDER) {
      byCategory[cat].sort((a, b) => a.apCost - b.apCost || a.name.localeCompare(b.name));
    }
    return byCategory;
  }, []);

  const visibleCategories = filter === 'all' ? CATEGORY_ORDER : [filter];

  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <div style={styles.headerRow}>
          <button style={styles.backButton} onClick={onBack}>← MENU</button>
          <h1 style={styles.title}>CARDS</h1>
          <div style={styles.headerSpacer} />
        </div>

        <div style={styles.intro}>
          Browse every implemented card. The card visuals below use the exact
          same <code style={styles.code}>.hand-card</code> component rendered in-game —
          hover any card to see its in-hand tooltip.
        </div>

        <div style={styles.filterRow}>
          <FilterButton active={filter === 'all'} onClick={() => setFilter('all')}>
            ALL
          </FilterButton>
          {CATEGORY_ORDER.map(cat => (
            <FilterButton
              key={cat}
              active={filter === cat}
              color={CATEGORY_COLORS[cat]}
              onClick={() => setFilter(cat)}
            >
              {CATEGORY_LABELS[cat].toUpperCase()}
            </FilterButton>
          ))}
        </div>

        {visibleCategories.map(cat => {
          const cards = grouped[cat];
          if (cards.length === 0) return null;
          return (
            <section key={cat} style={styles.section}>
              <div style={{ ...styles.sectionHeader, color: CATEGORY_COLORS[cat] }}>
                <span style={styles.sectionMark}>▍</span>
                {CATEGORY_LABELS[cat].toUpperCase()} · {cards.length}
              </div>
              <div style={styles.grid}>
                {cards.map((card, idx) => (
                  <CardEntry key={`${card.name}-${idx}`} card={card} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

// --- Filter button ---

interface FilterButtonProps {
  active: boolean;
  color?: string;
  onClick: () => void;
  children: React.ReactNode;
}

function FilterButton({ active, color = theme.colors.textSecondary, onClick, children }: FilterButtonProps) {
  return (
    <button
      style={{
        ...styles.filterButton,
        borderColor: active ? color : theme.colors.border,
        color: active ? color : theme.colors.textSecondary,
        background: active ? `${color}14` : 'transparent',
      }}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// --- Single card entry: the real hand-card visual + caption block ---

interface CardEntryProps {
  card: Omit<Card, 'id'>;
}

function CardEntry({ card }: CardEntryProps) {
  const categoryColor = CATEGORY_COLORS[card.category];
  const isMovement = card.movementRange !== undefined;

  return (
    <div style={styles.cardEntry}>
      {/*
        Real in-game hand card. Uses the .hand-card class straight from
        global.css so the visual (dimensions, background, border,
        hover lift, box-shadow) is identical to a card in hand. Children
        use the same inline style keys the GameScreen uses. The tooltip
        is the in-game .card-tooltip so hovering here shows exactly what
        hovering a card in hand shows.
      */}
      <div className="hand-card" style={cardHandStyles.container}>
        <div style={cardHandStyles.cost}>{card.apCost}</div>
        <div style={cardHandStyles.name}>{card.name}</div>
        <div style={cardHandStyles.category}>{card.category.toUpperCase()}</div>
        {isMovement && <div style={cardHandStyles.range}>{card.movementRange}m</div>}
        {card.isBurn && <div style={cardHandStyles.burnBadge}>BURN</div>}
        {card.isChain && <div style={cardHandStyles.chainBadge}>CHAIN</div>}
        <div style={cardHandStyles.categoryIcon}>
          <CardCategoryIcon category={card.category} size={16} />
        </div>
        {/* In-game .card-tooltip — appears on :hover via global.css */}
        <div className="card-tooltip">
          <div className="card-tooltip__header">
            <span className="card-tooltip__name">{card.name}</span>
            <span className="card-tooltip__cost">{card.apCost} AP</span>
          </div>
          <div className="card-tooltip__category">{card.category.toUpperCase()}</div>
          <div className="card-tooltip__desc">{card.description}</div>
          <div className="card-tooltip__stats">
            {card.movementRange !== undefined && (
              <div className="card-tooltip__stat">Range: {card.movementRange}m</div>
            )}
            {card.sweepRadius !== undefined && (
              <div className="card-tooltip__stat">Scan Radius: {(card.sweepRadius / 1000).toFixed(1)}km</div>
            )}
            {card.signalModifier !== undefined && card.signalModifier !== 1 && (
              <div className="card-tooltip__stat">
                Signal: {card.signalModifier === 0 ? 'None' : `${Math.round(card.signalModifier * 100)}%`}
              </div>
            )}
            {card.condition && <div className="card-tooltip__stat">{card.condition}</div>}
          </div>
        </div>
      </div>

      {/* Caption block — description + meta chips, shown below the card. */}
      <div style={styles.caption}>
        <div style={styles.captionHeader}>
          <span style={{ ...styles.captionName, color: categoryColor }}>
            {card.name}
          </span>
          <span style={styles.captionCost}>{card.apCost} AP</span>
        </div>
        <div style={styles.captionDesc}>{card.description}</div>
        {(isMovement || card.sweepRadius !== undefined || (card.signalModifier !== undefined && card.signalModifier !== 1) || card.condition || card.isBurn || card.isChain || card.isFieldcraft) && (
          <div style={styles.captionStats}>
            {isMovement && <span style={styles.statChip}>Range {card.movementRange}m</span>}
            {card.sweepRadius !== undefined && (
              <span style={styles.statChip}>Scan {(card.sweepRadius / 1000).toFixed(1)}km</span>
            )}
            {card.signalModifier !== undefined && card.signalModifier !== 1 && (
              <span style={styles.statChip}>
                Signal {card.signalModifier === 0 ? 'none' : `${Math.round(card.signalModifier * 100)}%`}
              </span>
            )}
            {card.isBurn && <span style={{ ...styles.statChip, color: '#ff8844', borderColor: '#ff884460' }}>BURN</span>}
            {card.isChain && <span style={{ ...styles.statChip, color: '#44aaff', borderColor: '#44aaff60' }}>CHAIN</span>}
            {card.isFieldcraft && <span style={{ ...styles.statChip, color: '#44cc88', borderColor: '#44cc8860' }}>BUFF</span>}
            {card.condition && <span style={styles.conditionText}>{card.condition}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Styles specific to the in-game hand card visual ---
// These mirror GameScreen.tsx exactly so the reference page and the
// in-game render stay consistent. The container only sets `position:
// relative` — everything else comes from the `.hand-card` class.
const cardHandStyles: Record<string, React.CSSProperties> = {
  container: {
    // .hand-card provides dimensions, background, border, layout, hover.
    // Override cursor so the reference page doesn't suggest clicking to play.
    cursor: 'default' as const,
  },
  cost: {
    position: 'absolute' as const,
    top: '6px',
    left: '8px',
    fontSize: '14px',
    fontWeight: 700,
    color: theme.colors.accentYellow,
  },
  name: {
    fontSize: '11px',
    fontWeight: 600,
    color: theme.colors.textPrimary,
    letterSpacing: '0.5px',
    textAlign: 'center' as const,
    padding: '0 6px',
  },
  category: {
    fontSize: '8px',
    letterSpacing: '1.5px',
    color: theme.colors.textDim,
  },
  range: {
    position: 'absolute' as const,
    bottom: '6px',
    right: '6px',
    fontSize: '9px',
    fontWeight: 600,
    color: theme.colors.accentBlue,
    letterSpacing: '0.5px',
  },
  categoryIcon: {
    position: 'absolute' as const,
    bottom: '4px',
    left: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.9,
  },
  burnBadge: {
    position: 'absolute' as const,
    top: '3px',
    right: '3px',
    background: 'rgba(255, 80, 50, 0.25)',
    color: '#ff5032',
    fontFamily: theme.fonts.mono,
    fontSize: '7px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    padding: '1px 3px',
    borderRadius: '2px',
  },
  chainBadge: {
    position: 'absolute' as const,
    top: '3px',
    right: '3px',
    background: 'rgba(68, 170, 255, 0.25)',
    color: '#44aaff',
    fontFamily: theme.fonts.mono,
    fontSize: '7px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    padding: '1px 3px',
    borderRadius: '2px',
  },
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(8, 12, 20, 0.98)',
    overflow: 'auto',
    fontFamily: theme.fonts.mono,
  },
  inner: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '32px 32px 120px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
  },
  headerSpacer: { flex: 1 },
  backButton: {
    background: 'rgba(255, 255, 255, 0.03)',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textSecondary,
    fontFamily: theme.fonts.mono,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '2px',
    padding: '8px 14px',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    letterSpacing: '6px',
    color: theme.colors.textPrimary,
    margin: 0,
  },
  intro: {
    fontSize: '11px',
    color: theme.colors.textDim,
    lineHeight: 1.6,
    maxWidth: '640px',
    marginBottom: '28px',
  },
  code: {
    display: 'inline-block',
    margin: '0 2px',
    padding: '0 4px',
    background: 'rgba(255, 255, 255, 0.04)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '3px',
    color: theme.colors.textSecondary,
  },
  filterRow: {
    display: 'flex',
    gap: '6px',
    marginBottom: '32px',
    flexWrap: 'wrap',
  },
  filterButton: {
    fontFamily: theme.fonts.mono,
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '2px',
    padding: '6px 12px',
    border: '1px solid',
    borderRadius: '3px',
    cursor: 'pointer',
    transition: 'all 0.12s',
  },
  section: {
    marginBottom: '48px',
  },
  sectionHeader: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '3px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  sectionMark: {
    fontSize: '16px',
    lineHeight: 1,
  },
  grid: {
    display: 'grid',
    // Each cell is the card width (90) + caption padding; min 140 so the
    // caption can wrap the description without running into the next column.
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '28px 20px',
    // Extra top padding so the hand-card hover lift (translateY(-8px)) and
    // tooltip have room to render without clipping against the section header.
    padding: '16px 0 0',
  },

  // --- Card entry: hand card on top, caption below ---
  cardEntry: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  },
  caption: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    textAlign: 'center',
  },
  captionHeader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'baseline',
    gap: '8px',
  },
  captionName: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.5px',
  },
  captionCost: {
    fontSize: '10px',
    fontWeight: 700,
    color: theme.colors.accentYellow,
    letterSpacing: '0.5px',
  },
  captionDesc: {
    fontSize: '10px',
    lineHeight: 1.5,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  captionStats: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: '2px',
  },
  statChip: {
    fontSize: '8px',
    fontWeight: 700,
    letterSpacing: '1px',
    padding: '2px 5px',
    borderRadius: '2px',
    border: `1px solid ${theme.colors.border}`,
    background: 'rgba(255, 255, 255, 0.02)',
    color: theme.colors.textDim,
  },
  conditionText: {
    fontSize: '9px',
    color: theme.colors.textDim,
    fontStyle: 'italic',
    flexBasis: '100%',
    marginTop: '2px',
  },
};
